import { Button, Card, CircularProgress, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLazyGetUserParticipatingProjectsPageQuery } from '../../api/projects'
import { useLazyGetUserTeamsPageQuery } from '../../api/teams'
import { useLazyGetUserGrowthToolsPageQuery } from '../../api/growthTools'
import { useLazyGetUserSocialOrdersPageQuery } from '../../api/socialOrders'
import type { IProjectsListItem } from '../../api/types/projects'
import type { TeamDescription } from '../../types/entitys/teams'
import type { IGrowthToolListItem } from '../../api/types/growthTools'
import type { ISocialOrderListItem } from '../../api/types/socialOrders'
import { CURSOR_LIST_PAGE_SIZE } from '../../consts/api'
import { routesProfile } from '../../consts'
import { ProjectCoverImage } from '../common/ProjectCoverImage'
import { UserAvatar } from '../common/UserAvatar'

type UserActivitySectionProps = {
  userId: number
}

export const UserParticipatingProjectsSection = ({ userId }: UserActivitySectionProps) => {
  const navigate = useNavigate()
  const [cursor, setCursor] = useState<string | null>(null)
  const [items, setItems] = useState<IProjectsListItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [trigger, { data, isLoading, isFetching, isError }] = useLazyGetUserParticipatingProjectsPageQuery()

  useEffect(() => {
    setCursor(null)
    setItems([])
    setNextCursor(null)
    trigger({ userId, limit: CURSOR_LIST_PAGE_SIZE, cursor: null }, true)
  }, [trigger, userId])

  useEffect(() => {
    if (cursor == null) return
    trigger({ userId, limit: CURSOR_LIST_PAGE_SIZE, cursor }, true)
  }, [cursor, trigger, userId])

  useEffect(() => {
    if (!data) return
    setNextCursor(data.meta?.next_cursor ?? null)
    setItems((prev) => (cursor ? [...prev, ...(data.items ?? [])] : (data.items ?? [])))
  }, [cursor, data])

  const canLoadMore = nextCursor != null && nextCursor !== '' && !isFetching

  return (
    <Card variant="primaryAdaptiveCard">
      <Stack gap={1.5}>
        <Typography variant="h3">Участвует в проектах</Typography>
        {isLoading && <LoadingLabel text="Загружаем проекты..." />}
        {isError && <Typography color="error">Не удалось загрузить проекты пользователя.</Typography>}
        {!isLoading && !isError && items.length === 0 && (
          <Typography variant="body1">Пользователь пока не участвует в проектах.</Typography>
        )}
        {items.length > 0 && (
          <Stack gap={1}>
            {items.map((project) => (
              <CompactRow
                key={project.id}
                image={<ProjectCoverImage src={project.image} alt={project.name} sx={compactImageSx} />}
                title={project.name}
                onClick={() => navigate(`/${routesProfile.PROJECTS.path}/${project.slug}`)}
              />
            ))}
          </Stack>
        )}
        {canLoadMore && <LoadMoreButton isFetching={isFetching} onClick={() => setCursor(nextCursor)} />}
      </Stack>
    </Card>
  )
}

export const UserTeamsSection = ({ userId }: UserActivitySectionProps) => {
  const navigate = useNavigate()
  const [cursor, setCursor] = useState<string | null>(null)
  const [items, setItems] = useState<TeamDescription[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [trigger, { data, isLoading, isFetching, isError }] = useLazyGetUserTeamsPageQuery()

  useEffect(() => {
    setCursor(null)
    setItems([])
    setNextCursor(null)
    trigger({ userId, limit: CURSOR_LIST_PAGE_SIZE, cursor: null }, true)
  }, [trigger, userId])

  useEffect(() => {
    if (cursor == null) return
    trigger({ userId, limit: CURSOR_LIST_PAGE_SIZE, cursor }, true)
  }, [cursor, trigger, userId])

  useEffect(() => {
    if (!data) return
    setNextCursor(data.meta?.next_cursor ?? null)
    setItems((prev) => (cursor ? [...prev, ...(data.items ?? [])] : (data.items ?? [])))
  }, [cursor, data])

  const canLoadMore = nextCursor != null && nextCursor !== '' && !isFetching

  return (
    <Card variant="primaryAdaptiveCard">
      <Stack gap={1.5}>
        <Typography variant="h3">Команды</Typography>
        {isLoading && <LoadingLabel text="Загружаем команды..." />}
        {isError && <Typography color="error">Не удалось загрузить команды пользователя.</Typography>}
        {!isLoading && !isError && items.length === 0 && (
          <Typography variant="body1">У пользователя пока нет команд.</Typography>
        )}
        {items.length > 0 && (
          <Stack gap={1}>
            {items.map((team) => (
              <CompactRow
                key={team.id}
                image={<UserAvatar src={team.image} size={36} />}
                title={team.title}
                onClick={() => navigate(`/${routesProfile.TEAMS.path}/${team.slug || team.id}`)}
              />
            ))}
          </Stack>
        )}
        {canLoadMore && <LoadMoreButton isFetching={isFetching} onClick={() => setCursor(nextCursor)} />}
      </Stack>
    </Card>
  )
}

export const UserSocialOrdersSection = ({ userId }: UserActivitySectionProps) => {
  const navigate = useNavigate()
  const [cursor, setCursor] = useState<string | null>(null)
  const [items, setItems] = useState<ISocialOrderListItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [trigger, { data, isLoading, isFetching, isError }] = useLazyGetUserSocialOrdersPageQuery()

  useEffect(() => {
    setCursor(null)
    setItems([])
    setNextCursor(null)
    trigger({ userId, limit: CURSOR_LIST_PAGE_SIZE, cursor: null }, true)
  }, [trigger, userId])

  useEffect(() => {
    if (cursor == null) return
    trigger({ userId, limit: CURSOR_LIST_PAGE_SIZE, cursor }, true)
  }, [cursor, trigger, userId])

  useEffect(() => {
    if (!data) return
    setNextCursor(data.meta?.next_cursor ?? null)
    setItems((prev) => (cursor ? [...prev, ...(data.items ?? [])] : (data.items ?? [])))
  }, [cursor, data])

  const canLoadMore = nextCursor != null && nextCursor !== '' && !isFetching

  return (
    <Card variant="primaryAdaptiveCard">
      <Stack gap={1.5}>
        <Typography variant="h3">Соц-заказы</Typography>
        {isLoading && <LoadingLabel text="Загружаем социальные заказы..." />}
        {isError && <Typography color="error">Не удалось загрузить социальные заказы пользователя.</Typography>}
        {!isLoading && !isError && items.length === 0 && (
          <Typography variant="body1">У пользователя пока нет социальных заказов.</Typography>
        )}
        {items.length > 0 && (
          <Stack gap={1}>
            {items.map((socialOrder) => (
              <CompactRow
                key={socialOrder.id}
                image={<ProjectCoverImage src={socialOrder.image} alt={socialOrder.title} sx={compactImageSx} />}
                title={socialOrder.title}
                onClick={() => navigate(`/${routesProfile.SOCIALORDERS.path}/${socialOrder.slug || socialOrder.id}`)}
              />
            ))}
          </Stack>
        )}
        {canLoadMore && <LoadMoreButton isFetching={isFetching} onClick={() => setCursor(nextCursor)} />}
      </Stack>
    </Card>
  )
}

export const UserGrowthToolsSection = ({ userId }: UserActivitySectionProps) => {
  const navigate = useNavigate()
  const [cursor, setCursor] = useState<string | null>(null)
  const [items, setItems] = useState<IGrowthToolListItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [trigger, { data, isLoading, isFetching, isError }] = useLazyGetUserGrowthToolsPageQuery()

  useEffect(() => {
    setCursor(null)
    setItems([])
    setNextCursor(null)
    trigger({ userId, limit: CURSOR_LIST_PAGE_SIZE, cursor: null }, true)
  }, [trigger, userId])

  useEffect(() => {
    if (cursor == null) return
    trigger({ userId, limit: CURSOR_LIST_PAGE_SIZE, cursor }, true)
  }, [cursor, trigger, userId])

  useEffect(() => {
    if (!data) return
    setNextCursor(data.meta?.next_cursor ?? null)
    setItems((prev) => (cursor ? [...prev, ...(data.items ?? [])] : (data.items ?? [])))
  }, [cursor, data])

  const canLoadMore = nextCursor != null && nextCursor !== '' && !isFetching

  return (
    <Card variant="primaryAdaptiveCard">
      <Stack gap={1.5}>
        <Typography variant="h3">Инструменты роста</Typography>
        {isLoading && <LoadingLabel text="Загружаем инструменты..." />}
        {isError && <Typography color="error">Не удалось загрузить инструменты роста пользователя.</Typography>}
        {!isLoading && !isError && items.length === 0 && (
          <Typography variant="body1">Пользователь пока не разместил инструменты роста.</Typography>
        )}
        {items.length > 0 && (
          <Stack gap={1}>
            {items.map((tool) => (
              <CompactRow
                key={tool.id}
                image={<ProjectCoverImage src={tool.image} alt={tool.title} sx={compactImageSx} />}
                title={tool.title}
                onClick={() => navigate(`/${routesProfile.GROWTHTOOLS.path}/${tool.slug || tool.id}`)}
              />
            ))}
          </Stack>
        )}
        {canLoadMore && <LoadMoreButton isFetching={isFetching} onClick={() => setCursor(nextCursor)} />}
      </Stack>
    </Card>
  )
}

const compactImageSx = { width: 36, height: 36, borderRadius: '8px', flexShrink: 0 }

const LoadingLabel = ({ text }: { text: string }) => (
  <Stack direction="row" gap={1.5} alignItems="center">
    <CircularProgress size={18} />
    <Typography variant="body1">{text}</Typography>
  </Stack>
)

const LoadMoreButton = ({ isFetching, onClick }: { isFetching: boolean; onClick: () => void }) => (
  <Stack direction="row" justifyContent="flex-end">
    <Button variant="follow" disabled={isFetching} onClick={onClick}>
      {isFetching ? 'Загрузка...' : 'Показать ещё'}
    </Button>
  </Stack>
)

const CompactRow = ({
  image,
  title,
  onClick,
}: {
  image: ReactNode
  title: string
  onClick: () => void
}) => (
  <Stack
    direction="row"
    gap={1.25}
    alignItems="center"
    sx={{ cursor: 'pointer', minWidth: 0 }}
    onClick={onClick}
  >
    {image}
    <Typography variant="body1" noWrap minWidth={0}>
      {title}
    </Typography>
  </Stack>
)
